import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Pencil } from 'lucide-react';
import { Badge, Card, Alert, Button, Input } from '@dreamtree-org/twreact-ui';
import { useAppApi } from '@/lib/api';
import Can from '@rbac/Can';
import DataTable from '@/components/DataTable';
import PageShell from '@/components/layout/PageShell';
import PageHeader from '@/components/layout/PageHeader';

/**
 * "Companies" (`/app-company`) — the records every tenant-scoped table's `company_id`
 * refers to. Before this screen a company was an opaque bigint with no name anywhere in
 * the product; this is where it gets one.
 *
 * Ids are meaningful and externally originated (commands/backfill-companies.js preserves
 * them), so `id` is shown as a column and is never editable.
 */
function apiError(ex) {
  return (ex.response && ex.response.data && ex.response.data.error) || ex.message;
}

const BLANK = { name: '', slug: '', description: '', is_active: true };

function CompanyForm({ editing, onSaved, onCancel }) {
  const { fetchApi } = useAppApi();
  const isNew = !editing.id;
  const [form, setForm] = useState(editing);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');

  const setField = (key, value) => setForm((f) => ({ ...f, [key]: value }));

  const save = async () => {
    setSaving(true);
    setErr('');
    const data = {
      name: form.name,
      slug: form.slug || null,
      description: form.description || null,
      is_active: !!form.is_active,
    };
    try {
      if (isNew) await fetchApi('crud', { model: 'AppCompany', action: 'create', data });
      else await fetchApi('crud', { model: 'AppCompany', action: 'update', where: { id: form.id }, data });
      onSaved();
    } catch (ex) {
      setErr(apiError(ex));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card className="p-3 space-y-3 border-primary-300">
      <div className="font-semibold">{isNew ? 'New company' : `Edit "${editing.name}"`}</div>
      {err && <Alert variant="error" title="Could not save">{err}</Alert>}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <Input label="Name" value={form.name || ''} onChange={(e) => setField('name', e.target.value)} />
        <Input label="Slug (optional)" value={form.slug || ''} placeholder="e.g. acme" onChange={(e) => setField('slug', e.target.value)} />
        <div className="sm:col-span-2">
          <Input label="Description" value={form.description || ''} onChange={(e) => setField('description', e.target.value)} />
        </div>
      </div>
      {!isNew && (
        <p className="text-xs text-secondary-500">
          Company id <code>{editing.id}</code> is referenced by every scoped table and can never change.
        </p>
      )}
      <div className="flex items-center gap-2">
        <label className="flex items-center gap-1 text-sm">
          <input type="checkbox" checked={!!form.is_active} onChange={(e) => setField('is_active', e.target.checked)} /> Active
        </label>
        <div className="flex-1" />
        <Button size="sm" variant="secondary" onClick={onCancel}>Cancel</Button>
        <Button size="sm" onClick={save} disabled={saving || !form.name}>{saving ? 'Saving…' : 'Save'}</Button>
      </div>
    </Card>
  );
}

function companyColumns(onEdit, nav) {
  return [
    {
      key: 'name',
      label: 'Name',
      render: (c) => (
        <div>
          <button onClick={() => nav(`/app-company/${c.id}`)} className="font-medium text-primary-600 hover:underline text-left">{c.name}</button>
          <div className="text-secondary-500 text-xs max-w-xs truncate" title={c.description}>{c.description}</div>
        </div>
      ),
    },
    { key: 'id', label: 'Company id', searchable: false, render: (c) => <span className="font-mono text-xs">{c.id}</span> },
    { key: 'slug', label: 'Slug', render: (c) => <span className="font-mono text-xs">{c.slug || '—'}</span> },
    {
      key: 'is_active',
      label: 'Status',
      searchable: false,
      render: (c) => (
        <Badge variant={c.is_active ? 'success' : 'secondary'} size="sm">
          {c.is_active ? 'active' : 'disabled'}
        </Badge>
      ),
    },
    {
      key: 'row_actions',
      label: '',
      sortable: false,
      searchable: false,
      render: (c) => (
        <Can module="companies" action="govern">
          <div className="whitespace-nowrap text-right">
            <button type="button" title="Edit" onClick={() => onEdit(c)} className="p-1 text-primary-600 hover:bg-primary-50 rounded">
              <Pencil size={14} />
            </button>
          </div>
        </Can>
      ),
    },
  ];
}

export default function CompaniesPage() {
  const { fetchApi } = useAppApi();
  const nav = useNavigate();
  const [companies, setCompanies] = useState([]);
  const [editing, setEditing] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');

  const reload = useCallback(() => {
    setLoading(true);
    return fetchApi('crud', {
      model: 'AppCompany', action: 'list',
      orderBy: { column: 'name', direction: 'asc' }, limit: 200,
    })
      .then((res) => setCompanies((Array.isArray(res) ? res : res?.data) || []))
      .catch((ex) => setErr(apiError(ex)))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { reload(); }, [reload]);

  const onSaved = () => { setEditing(null); setErr(''); reload(); };

  return (
    <PageShell className="space-y-3">
      <PageHeader
        title="Companies"
        subtitle={(
          <>
            {companies.length} compan{companies.length === 1 ? 'y' : 'ies'} — every agent, connection, knowledge base and call belongs to one.
          </>
        )}
        actions={(
          <Can module="companies" action="govern">
            <Button size="sm" onClick={() => setEditing({ ...BLANK })}>
              <Plus size={14} className="mr-1 inline" /> New company
            </Button>
          </Can>
        )}
      />

      {err && <Alert variant="error" title="Error">{err}</Alert>}
      {editing && <CompanyForm editing={editing} onSaved={onSaved} onCancel={() => setEditing(null)} />}

      <DataTable
        data={companies}
        columns={companyColumns(setEditing, nav)}
        loading={loading}
        emptyText="No companies yet — run `node commands/backfill-companies.js` to import the ids already in use."
        search={{ placeholder: 'Search companies…', keys: ['name', 'slug', 'description'] }}
        onReload={reload}
      />
    </PageShell>
  );
}
